import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import AnthropometricService from "../sections/anthropometric/AnthropometricService";
import LabInvestigationService from "../sections/labInvestigations/LabInvestigationService";
import { LI_V2_REFRESH } from "../sections/labInvestigations/events";
import { nameWithTag } from "../sections/labInvestigations/data";
import StepTrackerService from "../service/StepTrackerService";
import useIsOverflowing from "../helper/useIsOverflowing";
import { interpretationBand } from "../sections/anthropometric/data";
import { ANTH_V2_REFRESH } from "../sections/anthropometric/events";
import { formatShortDate } from "../sections/chiefComplaints/dateHelpers";
import { SECTION_SELECT } from "../sections/railEvents";
import StepTrendModal from "./StepTrendModal";
import "../styles/anthroOverview.scss";

/**
 * Anthropometric data — the right-hand card of the overview row. Source: Figma
 * Container 2428:4855 (inside screen "A1 Empty section" 2428:4702), read via Dev
 * Mode MCP.
 *
 * Not the Anthropometric *section*: that one is the 8-parameter table + editor
 * under sections/. This is the at-a-glance card that sits above the section
 * shell — five metric tiles, a daily-steps summary, then the lab panel grouped
 * elevated / lower / normal.
 *
 * The card is one fixed height, shared with the health card beside it: one row
 * of each block (styles/_overviewCards.scss). Its body scrolls inside the card,
 * so a long lab list never grows the page.
 * UI-only: everything below is hardcoded sample data and both buttons are inert.
 */

/* ---- sample data ----
 * Transcribed value-for-value from 2428:4855. Every tile on screen is mapped out
 * of these three consts, so a real feed only has to match their shape. */

/**
 * Fixed spec for the top-row tiles. `key` matches the backend parameter enum
 * so the API response can be looked up by that field. Only 5 of the 7
 * anthropometric parameters appear in the summary — Blood group and Hip
 * circumference are excluded per UX spec (Hip is denominator-only, Blood
 * group has no useful summary display).
 */
const METRIC_SPEC = [
  { key: "height", label: "Height" },
  { key: "weight", label: "Weight" },
  { key: "bmi", label: "BMI" },
  { key: "waistCircumference", label: "Waist" },
  { key: "whr", label: "WHR" },
];

/**
 * Daily step counts — LIVE from api-sleep-step-tracker-service via
 * `useRecentSteps` (below). Sample data lives here for reference so a
 * fresh reader can see the shape the render expects — but the render
 * never uses this const anymore. Left in place because Anthropometric
 * data + Lab investigations blocks in this file still use their sample
 * consts, and yanking this one out would be inconsistent.
 */
const STEPS_SAMPLE = [
  { id: "s1", label: "Step count", value: "6,420", date: "14 Jun 2026" },
  { id: "s2", label: "Step count", value: "3,230", date: "13 Jun 2026" },
  { id: "s3", label: "Step count", value: "6,445", date: "12 Feb 2026" },
];

/**
 * "Fresh" data window — data newer than this many days counts as
 * current; anything older triggers the "ask the patient to sync" note
 * alongside the tiles. 7 was picked by product spec (2026-08-26).
 */
const FRESH_WINDOW_DAYS = 7;

/**
 * Pull step history over a long window (2 years) and expose:
 *   - `tiles`     — up to 3 most-recent day-count tiles (any age)
 *   - `latestISO` — ISO of the newest data point (or null when empty).
 *                   Fed into StepTrendModal as the default month so
 *                   the trend graph opens on the month with data.
 *   - `isStale`   — true when either there's no data, OR the newest
 *                   data point is older than FRESH_WINDOW_DAYS. Drives
 *                   the "Recent data not available" message in the
 *                   header — shown even alongside stale-but-existent
 *                   tiles as a "this hasn't been updated" hint.
 *
 * Empty response, transport error, or a patient that never synced all
 * land in `{ tiles: [], latestISO: null, isStale: true }`.
 */
const useRecentSteps = (patientId) => {
  const service = useRef(new StepTrackerService()).current;
  const [state, setState] = useState({
    loading: true, tiles: [], latestISO: null, isStale: true,
  });

  useEffect(() => {
    if (!patientId) {
      setState({ loading: false, tiles: [], latestISO: null, isStale: true });
      return undefined;
    }
    let cancelled = false;
    const to = new Date();
    // 2-year window — wide enough to always surface the last three
    // syncs for any patient with any data at all. Payload is trivial
    // (map of ~day keys → int), so overshooting is cheap.
    const from = new Date(to.getTime() - 730 * 24 * 60 * 60 * 1000);
    setState({ loading: true, tiles: [], latestISO: null, isStale: true });
    service.getSteps(
      patientId,
      from.toISOString(),
      to.toISOString(),
      (res) => {
        if (cancelled) return;
        const m = (res && res.steps) || {};
        // Convert the map into an array + drop zero-count days (the
        // backend filters `steps: {$ne: 0}` server-side, but be
        // defensive in case an aggregated bucket lands as 0).
        const rows = Object.keys(m)
          .map((iso) => ({ iso, value: Number(m[iso] || 0) }))
          .filter((r) => r.value > 0)
          .sort((a, b) => (a.iso < b.iso ? 1 : -1));    // newest first
        const tiles = rows.slice(0, 3).map((r, i) => ({
          id:    `s${i + 1}`,
          label: "Step count",
          value: r.value.toLocaleString("en-US"),
          date:  formatShortDate(r.iso),
        }));
        const latestISO = rows.length > 0 ? rows[0].iso : null;
        // Newest data point older than FRESH_WINDOW_DAYS → stale.
        // Missing latestISO also counts as stale (nothing to show).
        const staleCutoff = to.getTime() - FRESH_WINDOW_DAYS * 24 * 60 * 60 * 1000;
        const isStale =
          !latestISO || new Date(latestISO).getTime() < staleCutoff;
        setState({ loading: false, tiles, latestISO, isStale });
      },
      () => {
        if (!cancelled) {
          setState({ loading: false, tiles: [], latestISO: null, isStale: true });
        }
      },
    );
    return () => { cancelled = true; };
  }, [patientId, service]);

  return state;
};

/* Lab investigations — surfaces "out of range" readings from LI v2 for the
 * at-a-glance card. Only flagged rows (server-computed flag ∈ high/low/abnormal)
 * are shown; a normal / unknown / user-picked-None row would just add noise to a
 * summary. Grouping was intentionally dropped after product review: a flat grid
 * of flagged tiles is what surgeons scanned fastest in the mock. Backend has no
 * trend/tier fields; the pill shows the current Range label instead of the old
 * "Very High / Borderline" tiers.
 *
 * Tone mapping (drives tile colour + pill background):
 *   • high     → elevated (red)
 *   • abnormal → elevated (red)  ← categorical "abnormal" from user pick
 *   • low      → lower    (amber)
 * Nothing else is rendered.
 */
const FLAGGED_SET = new Set(["high", "low", "abnormal"]);
const TONE_FOR_FLAG = { high: "elevated", abnormal: "elevated", low: "lower" };
const PILL_LABEL = { high: "High", low: "Low", abnormal: "Abnormal" };

/* ---- glyphs ----
 * The board sets these in the Material Icons font (calendar_today, arrow_upward,
 * arrow_downward, horizontal_rule). That font isn't loaded anywhere in phr — only
 * Montserrat is — so they're inlined as SVG on the Material 24px grid instead.
 * Size and colour come from CSS. */

/** Material `calendar_today`. */
const CalendarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z" />
  </svg>
);
CalendarIcon.propTypes = { className: PropTypes.string };

/** The dated footnote shared by the metric and step tiles. */
const RecordedDate = ({ date }) => (
  <span className="ao-date">
    <CalendarIcon className="ao-date-icon" />
    {date || "—"}
  </span>
);
RecordedDate.propTypes = { date: PropTypes.string };

/* ---- tiles ---- */

/**
 * One headline measurement (2428:4862). Renders an empty state (dashes)
 * when the parameter has no recorded value yet. The interpretation pill only
 * renders when the classifier produced something meaningful (`unknown` is
 * hidden — matches the section table's "Reads as" column behaviour).
 */
const MetricTile = ({ metric }) => {
  const { label, value, unit, pill, band, date } = metric;
  // Long BMI readings ("277.78") can clip past the tile's narrow value
  // column — surface a white-bg / black-text tooltip on hover with the
  // full value (including unit) so the reviewer never loses precision
  // to the ellipsis. Same mechanism LabTile uses (.li-dm-tt + data-tt).
  // The ref lives on the ellipsis carrier (.ao-metric-value); the
  // tooltip anchor sits on a wrapper OUTSIDE .ao-metric-measure since
  // that span's overflow:hidden would otherwise clip the ::after pop.
  const [valueRef, valueOverflowing] = useIsOverflowing(value);
  const valueTt = value
    ? (unit ? `${value} ${unit}` : String(value))
    : undefined;
  return (
    <div className="ao-metric">
      <span className="ao-metric-label">{label}</span>
      <div className="ao-metric-row">
        {/* Value + unit share one flex child so space-between still
            keeps "177.8 cm" on the left and the pill on the right.
            Unit is skipped for unitless entries (WHR ratio, Blood
            group's "None" placeholder). */}
        <span
          className="ao-metric-measure-wrap li-dm-tt"
          data-tt={valueOverflowing ? valueTt : undefined}
        >
          <span className="ao-metric-measure">
            <span ref={valueRef} className="ao-metric-value">{value || "—"}</span>
            {value && unit ? (
              <span className="ao-metric-unit">{unit}</span>
            ) : null}
          </span>
        </span>
        {pill && band ? (
          <span
            className="ao-metric-pill"
            style={{ background: band.bg, color: band.fg }}
          >
            {pill}
          </span>
        ) : null}
      </div>
      <RecordedDate date={date} />
    </div>
  );
};
MetricTile.propTypes = {
  metric: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    unit: PropTypes.string,
    pill: PropTypes.string,
    band: PropTypes.shape({ bg: PropTypes.string, fg: PropTypes.string }),
    date: PropTypes.string,
  }).isRequired,
};

/** One day's step count (2428:4909). */
const StepTile = ({ step }) => (
  <div className="ao-step">
    <span className="ao-step-label">{step.label}</span>
    <span className="ao-step-value">{step.value}</span>
    <RecordedDate date={step.date} />
  </div>
);
StepTile.propTypes = {
  step: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
  }).isRequired,
};

/** Material Symbols Rounded `steps` (400, unfilled), on that set's 960 grid. */
const StepsIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
    <path d="M216-580q39 0 74 14t64 41l382 365h24q17 0 28.5-11.5T800-200q0-8-1.5-17T788-235L605-418l-71-214-74 18q-38 10-69-14t-31-63v-84l-28-14-154 206q-1 1-1 1.5t-1 1.5h40Zm0 80h-46q3 7 7.5 13t10.5 11l324 295q11 11 25 16t29 5h54L299-467q-17-17-38.5-25t-44.5-8ZM566-80q-30 0-57-11t-50-31L134-417q-46-42-51.5-103T114-631l154-206q17-23 45.5-30.5T368-861l28 14q21 11 32.5 30t11.5 42v84l74-19q30-8 58 7.5t38 44.5l65 196 170 170q20 20 27.5 43t7.5 49q0 50-35 85t-85 35H566Z" />
  </svg>
);

/**
 * No step data at all (Figma 6107:14621): one card where the three tiles would
 * be, a ghost chart beside the copy. The head above it keeps its own "ask the
 * patient to sync" note, as the board does.
 */
const StepsEmpty = () => (
  <div className="ao-steps-empty">
    <div className="ao-steps-empty-head">
      <span className="ao-step-label">Step count</span>
      <span className="ao-steps-empty-dash">—</span>
    </div>
    <div className="ao-steps-empty-body">
      <span className="ao-steps-empty-art" aria-hidden="true">
        <span className="ao-steps-empty-baseline" />
        <span className="ao-steps-empty-bars">
          <span />
          <span />
          <span />
          <span />
          <span />
        </span>
        <span className="ao-steps-empty-goal" />
        <span className="ao-steps-empty-disc">
          <StepsIcon />
        </span>
      </span>
      <div className="ao-steps-empty-copy">
        <p className="ao-steps-empty-title">No step data yet</p>
        <p className="ao-steps-empty-note">Recent data not available, ask the patient to sync</p>
      </div>
    </div>
    <span className="ao-steps-empty-spacer" aria-hidden="true" />
  </div>
);

/** One flagged lab reading. `tone` derives from `flag` (see TONE_FOR_FLAG).
 *  Trend arrow + reference-range line intentionally dropped from the original
 *  spec — LI v2 doesn't compute a trend, and the ref-range copy was noisy in
 *  the summary. Pill shows the current Range label ("High" / "Low" /
 *  "Abnormal") — matches the vocabulary in the Digitise / Correction pickers.
 */
/**
 * Returns [ref, isOverflowing] — attach the ref to the element that
 * carries `overflow: hidden; text-overflow: ellipsis`, and read the
 * flag to decide whether to surface a tooltip.
 *
 * Measures after layout via useLayoutEffect and re-measures on parent
 * resize (viewport, rail collapse, tile row reflow) via ResizeObserver.
 * Also re-runs whenever the tracked text prop changes.
 */
const LabTile = ({ tile, tone }) => {
  // .li-dm-tt (defined in sections.scss, globally imported by PatientShell)
  // renders a white-bg / black-text tooltip on hover from a `data-tt`
  // attribute — but ONLY when data-tt is present (the CSS selector is
  // .li-dm-tt[data-tt]:hover::after). We always keep the class (harmless
  // — just position: relative) and toggle data-tt on actual truncation
  // so short names / values don't fire a redundant tooltip.
  //
  // Trigger anchors on the head + a number wrapper (both have no
  // overflow: hidden of their own) — the inner ellipsis carriers
  // .ao-lab-name / .ao-lab-value would otherwise clip the ::after.
  const [nameRef, nameOverflowing] = useIsOverflowing(tile.name);
  const [valueRef, valueOverflowing] = useIsOverflowing(tile.value);
  const valueTt = tile.unit ? `${tile.value} ${tile.unit}` : String(tile.value);
  return (
    <div className={`ao-lab-tile tone-${tone}`}>
      <div className="ao-lab-head li-dm-tt" data-tt={nameOverflowing ? tile.name : undefined}>
        <span ref={nameRef} className="ao-lab-name">{tile.name}</span>
      </div>
      <div className="ao-lab-row">
        <span className="ao-lab-number-wrap li-dm-tt" data-tt={valueOverflowing ? valueTt : undefined}>
          <span className="ao-lab-number">
            <span ref={valueRef} className="ao-lab-value">{tile.value}</span>
            {tile.unit ? <span className="ao-lab-unit">{tile.unit}</span> : null}
          </span>
        </span>
        <span className="ao-lab-pill">{tile.pill}</span>
      </div>
      <span className="ao-lab-recorded">
        <CalendarIcon className="ao-lab-recorded-icon" />
        Last recorded · {tile.date}
      </span>
    </div>
  );
};
LabTile.propTypes = {
  tile: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    unit: PropTypes.string,
    pill: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
  }).isRequired,
  tone: PropTypes.oneOf(["elevated", "lower"]).isRequired,
};

const AnthroOverviewCard = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const steps = useRecentSteps(patientId);
  const [trendOpen, setTrendOpen] = useState(false);

  // Summary payload — a subset of the full anthropometrics doc (statusHistory
  // stripped server-side via ?view=summary).
  const [parameters, setParameters] = useState([]);

  const load = useCallback(() => {
    if (!patientId) return;
    const svc = new AnthropometricService();
    svc.summary(
      patientId,
      (res) => setParameters((res && res.parameters) || []),
      () => setParameters([])
    );
  }, [patientId]);

  useEffect(() => {
    load();
    // Anthro is a projection over api-lab-investigation-v2 (ADR-0001 Phase 4).
    // Listen for both signals so this tile block refreshes when either the
    // Anthro section saves (ANTH_V2_REFRESH) or the LI section writes to a
    // mapped SLT (LI_V2_REFRESH) — otherwise a Weight write done from the
    // Lab-investigations section would leave the dashboard tile stale.
    EventService.addListener(ANTH_V2_REFRESH, load);
    EventService.addListener(LI_V2_REFRESH, load);
    return () => {
      EventService.removeListener(ANTH_V2_REFRESH, load);
      EventService.removeListener(LI_V2_REFRESH, load);
    };
  }, [load]);

  // Build the 5 top tiles by mapping the fixed spec onto the API response.
  // Parameters not returned by the API (never recorded) render as empty.
  const metricTiles = useMemo(() => {
    const byKey = {};
    for (const p of parameters) byKey[p.parameter] = p;
    return METRIC_SPEC.map((spec) => {
      const entry = byKey[spec.key];
      const current = entry && entry.current;
      if (!current) {
        return { key: spec.key, label: spec.label };
      }
      const interpretation = current.interpretation || "unknown";
      const band = interpretationBand(interpretation);
      const hasBand = interpretation && interpretation !== "unknown";
      // Unit lives on `current.unit` in the LI-backed projection.
      // "None" is the sentinel value the Anthro→LI adapter writes for
      // ratios / categoricals (WHR, Blood group) — treat it as "no
      // unit" so the tile doesn't render the placeholder verbatim.
      const rawUnit = String(current.unit || "").trim();
      const unit = rawUnit && rawUnit.toLowerCase() !== "none" ? rawUnit : "";
      // The unit is drawn beside the value, so the value must not carry
      // it too. The LI-backed projection sends a bare "173"; readings
      // stored before it keep the editor's UI-shaped "173 cm", which read
      // "173 cm cm" here. Drop a trailing " <unit>", as the section table
      // does (Anthropometric.js).
      const display = String(current.valueDisplay || "");
      const value = unit && display.endsWith(` ${unit}`) ? display.slice(0, -(unit.length + 1)) : display;
      return {
        key: spec.key,
        label: spec.label,
        value,
        unit,
        pill: hasBand ? band.label : null,
        band: hasBand ? band : null,
        date: current.testDate ? formatShortDate(current.testDate) : "",
      };
    });
  }, [parameters]);

  // Lab investigations — LIVE from api-lab-investigation-v2-service. Only
  // flagged rows are surfaced (high / low / abnormal); everything else stays
  // hidden. `loading` gates the "no data" copy so the empty state doesn't
  // flash before the request lands.
  //
  // Source of truth: top-level `inv.{flag, value, testDate, test.unit}`. The
  // backend rewrites these on every append/update/correction so they always
  // mirror the LATEST WRITE — same field the section table uses to compute
  // its "N flagged" chip, so the dashboard count matches the section 1:1.
  //
  // (Known drift: a later-added but earlier-dated reading overwrites the
  // top-level — a backend bug in appendReadingFromAdd/Batch. When that's
  // fixed at the source, both the section and dashboard fix together.)
  const [labState, setLabState] = useState({ loading: true, flagged: [] });
  const loadLabs = useCallback(() => {
    if (!patientId) {
      setLabState({ loading: false, flagged: [] });
      return;
    }
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (res) => {
        const rows = (res && res.investigations) || [];
        const flagged = rows
          .filter((inv) => !inv.deleted)
          .map((inv) => {
            const flag = (inv.flag || "").toLowerCase();
            if (!FLAGGED_SET.has(flag)) return null;
            const test = inv.test || {};
            return {
              id: inv.investigationId,
              // The tile shows the latest reading, so its tag qualifies the name.
              name: nameWithTag(test, "Unknown test"),
              value: inv.value || "—",
              unit: test.unit || "",
              pill: PILL_LABEL[flag] || flag,
              tone: TONE_FOR_FLAG[flag] || "elevated",
              date: inv.testDate ? formatShortDate(inv.testDate) : "",
              testDateISO: inv.testDate || "",
            };
          })
          .filter(Boolean)
          // Newest reading first; break ties by name so the order is stable.
          .sort((a, b) => {
            if (a.testDateISO !== b.testDateISO) return a.testDateISO < b.testDateISO ? 1 : -1;
            return a.name.localeCompare(b.name);
          });
        setLabState({ loading: false, flagged });
      },
      () => setLabState({ loading: false, flagged: [] })
    );
  }, [patientId]);

  useEffect(() => {
    loadLabs();
    // Section-side saves emit LI_V2_REFRESH; Anthro saves also mutate LI
    // via the Phase-4 projection so ANTH_V2_REFRESH must refresh the
    // flagged-labs list too — otherwise a Blood-group / Waist / Hip write
    // from the Anthro rail wouldn't propagate here without a page reload.
    EventService.addListener(LI_V2_REFRESH, loadLabs);
    EventService.addListener(ANTH_V2_REFRESH, loadLabs);
    return () => {
      EventService.removeListener(LI_V2_REFRESH, loadLabs);
      EventService.removeListener(ANTH_V2_REFRESH, loadLabs);
    };
  }, [loadLabs]);

  const outOfRange = labState.flagged.length;

  /* View all → jump to the Lab investigations section, the same way the
     medicines drug-allergies bar opens Allergies: SECTION_SELECT rather than
     threading a callback down through this card. PatientDetails owns the
     active-section state and listens for it. The button was inert before, with
     a title that admitted as much. */
  const openLabSection = useCallback(() => {
    EventService.emit(SECTION_SELECT, "lab-investigations");
  }, []);

  return (
    <section
      className="ao-card"
      aria-label={patient && patient.name ? `Anthropometric data for ${patient.name}` : "Anthropometric data"}
    >
      <div className="ao-body">
        {/* The heading scrolls WITH the content (product, 2026-09-21): pinned
            outside the scroller it stayed on screen over the lab tiles as if
            they were anthropometric data. Not sticky — it scrolls away. */}
        <h2 className="ao-title">Anthropometric data</h2>

        <div className="ao-metrics">
          {metricTiles.map((m) => (
            <MetricTile key={m.key} metric={m} />
          ))}
        </div>

        <div className="ao-activity">
          <div className="ao-activity-head">
            <h3 className="ao-subtitle">Physical activity (daily steps)</h3>
            {/* Note surfaces the state in one of three modes:
                  - loading  → "Loading recent steps…"
                  - stale    → "Recent data not available…" (fires for
                    both empty and >7-days-old data — the clinician
                    should ask the patient to sync in both cases)
                  - fresh    → no note, tiles are the primary signal */}
            {steps.loading ? (
              <p className="ao-activity-note">Loading recent steps…</p>
            ) : steps.isStale ? (
              <p className="ao-activity-note">
                Recent data not available, ask the patient to sync
              </p>
            ) : null}
            {/* View trend hidden when there's nothing to graph at all.
                Stale-but-existent data still gets the button so the
                clinician can inspect historical trends. */}
            {steps.tiles.length > 0 ? (
              <button
                type="button"
                className="ao-ghost-btn"
                onClick={() => setTrendOpen(true)}
                title="View monthly step trend"
              >
                View trend
              </button>
            ) : null}
          </div>
          {steps.tiles.length > 0 ? (
            <div className="ao-steps">
              {steps.tiles.map((s) => (
                <StepTile key={s.id} step={s} />
              ))}
            </div>
          ) : !steps.loading ? (
            <StepsEmpty />
          ) : null}
        </div>

        {trendOpen && patientId ? (
          <StepTrendModal
            patientId={patientId}
            // Open on the month that actually has the latest data —
            // otherwise a patient who last synced 3 months ago would
            // land on a blank current-month graph.
            initialISO={steps.latestISO}
            onClose={() => setTrendOpen(false)}
          />
        ) : null}

        <div className="ao-labs">
          <div className="ao-labs-head">
            <h3 className="ao-subtitle">Lab investigations</h3>
            <span className="ao-rule" aria-hidden="true" />
            <span className="ao-labs-summary">
              <span
                className={`ao-dot tone-${outOfRange > 0 ? "elevated" : "normal"}`}
                aria-hidden="true"
              />
              {outOfRange} {outOfRange === 1 ? "parameter" : "parameters"} out of range
            </span>
            <button
              type="button"
              className="ao-ghost-btn"
              onClick={openLabSection}
              title="Open the Lab investigations section"
            >
              View all
            </button>
          </div>

          <div className="ao-lab-group">
            {labState.loading ? (
              <p className="ao-activity-note">Loading lab investigations…</p>
            ) : labState.flagged.length === 0 ? (
              <p className="ao-activity-note">
                No out-of-range investigations recorded
              </p>
            ) : (
              <div className="ao-lab-grid">
                {labState.flagged.map((t) => (
                  <LabTile key={t.id} tile={t} tone={t.tone} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

AnthroOverviewCard.propTypes = {
  // Accepted so the card can be wired to a real patient later; the sample data
  // above stands in until then, so today it only names the region.
  patient: PropTypes.object,
};

export default AnthroOverviewCard;
